import { DropzoneFile } from 'components/Dropzone/utils';
import { ObjectValues } from 'types';
import { DataRoom, DataRoomSecuritySettings } from './DataRoom';
import { DataRoomMember } from './DataRoomMember';
import { User } from 'models/User';
import { DataRoomNode } from './DataRoomNode';
import { CurrentLocation } from 'components/DataRoomNodeCurrentLocation/DataRoomNodeCurrentLocation.component';
import { NotificationMedium } from 'models/Notification';

export const DATA_ROOM_RFI_FILE_TYPES: Record<string, string> = {
  IM: 'im',
  PL: 'pl',
  ASSET_REGISTER: 'asset_register',
  BALANCE_SHEET: 'balance_sheet',
  CUSTOMER_LIST: 'customer_list',
  SUPPLIER_LIST: 'supplier_list',
  TAX_FILINGS: 'tax_filings',
  SHAREHOLDER_REGISTER: 'shareholder_register',
} as const;

export const ADDITIONAL_DOCUMENTS = 'additional_documents';

export const DATA_ROOM_RFI_FILE_TYPES_WITHOUT_PERIOD = [
  DATA_ROOM_RFI_FILE_TYPES.IM,
  DATA_ROOM_RFI_FILE_TYPES.ASSET_REGISTER,
  DATA_ROOM_RFI_FILE_TYPES.CUSTOMER_LIST,
  DATA_ROOM_RFI_FILE_TYPES.SUPPLIER_LIST,
  DATA_ROOM_RFI_FILE_TYPES.SHAREHOLDER_REGISTER,
];

export const DATA_ROOM_RFI_FILE_PERIOD = {
  '2021': '2021',
  '2022': '2022',
  '2023': '2023',
  '2024': '2024',
  LAST_3_FINANCIAL_YEARS: 'last_three_financial_years',
} as const;

export const DATA_ROOM_RFI_STATUS = {
  PENDING: 'pending',
  SUBMITTED: 'submitted',
  CLOSED: 'closed',
} as const;

export const DATA_ROOM_RFI_FILE_STATUS = {
  UPLOADED: 'uploaded',
  ADDED: 'added',
  IGNORED: 'ignored',
} as const;

export type DataRoomRFIFileStatus = ObjectValues<
  typeof DATA_ROOM_RFI_FILE_STATUS
>;

export type DataRoomRFIFilePeriod = ObjectValues<
  typeof DATA_ROOM_RFI_FILE_PERIOD
>;

export type DataRoomRFIStatus = ObjectValues<typeof DATA_ROOM_RFI_STATUS>;

export type DataRoomRFIUploadFileTypes = ObjectValues<
  typeof DATA_ROOM_RFI_FILE_TYPES
>;

export type DataRoomRFI = {
  _id: string;

  /* ----------------------------- Core properties ---------------------------- */

  name: string;
  notifyOnComplete: boolean;
  notificationMedium: NotificationMedium[];
  dueDate: Date;
  twentyFourHoursBeforeDeadlineReminder: boolean;
  atDeadlineReminder: boolean;
  threeDaysAfterDeadlineReminder: boolean;
  fiveDaysAfterDeadlineReminder: boolean;
  recipients: string[];
  customNote: string;
  status: DataRoomRFIStatus;
  externalKey: string;

  automaticFileUploadToDataRoom: boolean;
  grantAccessDataRoomMemberIds: string[];

  brokerMessages: BrokerMessageType[];

  numberOfViews: number;

  allRecipientsWithReminders: DataRoomRfiRecipient[];

  lastSubmittedAt?: Date; // new file uploaded or new message arrived for broker

  // By user
  lastOpenedAt?: Date;

  // Metadata
  createdAt: Date;
  updatedAt: Date;

  /* -------------------- Virtual (non-database) properties ------------------- */
  dataRoomRFICategories: DataRoomRFICategory[];
  totalCategories: number;
  completedCategories: number;
  displayStatus: DataRoomRFIDisplayStatus;
  uploadStatus: DataRoomRFIUploadStatus;
  additionalDocumentsCategory?: DataRoomRFICategory;
  creationType: DataRoomRFICreationType;

  userId: string;
  user: Partial<User> | null;

  dataRoomMemberId: string;
  dataRoomMember: Partial<DataRoomMember> | null;

  dataRoomId: string;
  dataRoom: Partial<DataRoom> | null;
} & DataRoomSecuritySettings;

export type DataRoomRFICategory = {
  _id: string;

  name: string;
  period: DataRoomRFIFilePeriod;
  // New
  files: FileTypeRFI[];
  externalKey: string;
  type: DataRoomRFIUploadFileTypes;

  assignedTo?: string;
  reminders: { email: string; sentAt: Date }[];

  fileCount: number;
  uploadStatus: DataRoomRFIUploadStatus;
  uploadedOn?: Date;
  uploadedBy?: Partial<DataRoomRFIFileUploader>[] | null;

  note?: string;

  dataRoomNodeId?: string | null; // folder where files will be uploaded

  dataRoomRFIId: string;
  dataRoomRFI: Partial<DataRoomRFI> | null;

  dataRoomId: string;
  dataRoom: Partial<DataRoom> | null;
};

export type FileTypeRFI = {
  _id: string;

  file: DropzoneFile;
  status: DataRoomRFIFileStatus;
  description?: string;

  dataRoomNodeId: string;
  dataRoomNode: Partial<DataRoomNode> | null;

  dataRoomRFIFileUploaderId: string;
  dataRoomRFIFileUploader: Partial<DataRoomRFIFileUploader> | null;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
} & CurrentLocation;

export type DataRoomRFIFileUploader = {
  _id: string;

  email: string;
  firstName: string;
  lastName: string;
  companyName: string;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
};

export type BrokerMessageType = {
  content: string;
  addedAt: Date;

  dataRoomRFIFileUploaderId?: string;
  dataRoomRFIFileUploader?: Partial<DataRoomRFIFileUploader> | null;
};

export type SubmitDataRoomRFIPayload = {
  optionalNotes: {
    dataRoomRFIFileId: string;
    description: string;
  }[];
  brokerMessage: string;
};

export const CATEGORY_SORTING_VALUES = {
  FILE_NAME: 'fileName',
  UPLOADER: 'uploader',
};

export type DataRoomRfiRecipient = {
  email: string;
  forwarded: boolean;
  reminders: Date[];
};

export type DataRoomRfiTemplate = {
  _id: string;

  name: string;
  documents: {
    name: string;
    period: DataRoomRFIFilePeriod;
  }[];
  notifyOnComplete: boolean;
  notificationMedium: NotificationMedium[];
  customNote: string;

  userId: string;
  user: Partial<User>;

  createdAt: Date;
};

export type DataRoomMembersAccessGroup = {
  id: string;
  name: string;
  members: DataRoomMember[];
};

export type RfiCreateStep = { isCreatingOrEditingTemplate: boolean };

export type RfiDocumentOption = {
  name: string;
  period: DataRoomRFIFilePeriod;
  checked?: boolean;
};

export const DATA_ROOM_RFI_DISPLAY_STATUS = {
  NOT_STARTED: 'not_started',
  PARTIALLY_COMPLETED: 'partially_completed',
  COMPLETED: 'completed',
  CLOSED: 'closed',
} as const;

export type DataRoomRFIDisplayStatus = ObjectValues<
  typeof DATA_ROOM_RFI_DISPLAY_STATUS
>;

export const DATA_ROOM_RFI_UPLOAD_STATUS = {
  UPLOADED: 'uploaded',
  NOT_UPLOADED: 'not_uploaded',
} as const;

export type DataRoomRFIUploadStatus = ObjectValues<
  typeof DATA_ROOM_RFI_UPLOAD_STATUS
>;

export const DATA_ROOM_RFI_CREATION_TYPE = {
  CHECKLIST: 'checklist',
  SPREADSHEET: 'spreadsheet',
} as const;

export type DataRoomRFICreationType = ObjectValues<
  typeof DATA_ROOM_RFI_CREATION_TYPE
>;

export const RFI_SEND_REMINDER_TRIGGER = {
  REQUESTS_LIST: 'requests_list',
  DOCUMENTS_TABLE: 'documents_table',
  SELECTION_BAR: 'selection_bar',
  SETTINGS_DRAWER: 'settings_drawer',
  CATEGORY_DRAWER: 'category_drawer',
} as const;

export type RfiSendReminderTrigger = ObjectValues<
  typeof RFI_SEND_REMINDER_TRIGGER
>;

export type DataRoomRFIStatusCounts = {
  notStarted: number;
  partiallyCompleted: number;
  completed: number;
  closed: number;
};

export const RFI_ACTIVITY_TAB = {
  ADDITIONAL_FILES: 'additional_files',
  MESSAGES: 'messages',
} as const;

export type RfiActivityTab = ObjectValues<typeof RFI_ACTIVITY_TAB>;

export const PARSED_SPREADSHEET_RFI_ROW_STATUS = {
  READY_TO_ADD: 'ready_to_add',
  NEEDS_ATTENTION: 'needs_attention',
} as const;

export type ParsedSpreadsheetRFIRowStatus = ObjectValues<
  typeof PARSED_SPREADSHEET_RFI_ROW_STATUS
>;

export type ParsedSpreadsheetRFIRow = {
  document: string;
  assignedTo: string;
  note: string;
  status: ParsedSpreadsheetRFIRowStatus;
  errors: string[];
};
